import Button, { ButtonType } from "components/Elements/Buttonv2";
import Input from "components/Elements/Inputv2";
import searchIconImage from "./svg/search-icon.svg";
import threeDotsIconImage from "./svg/three-dots-icon.svg";
import emptyDataImage from "./svg/empty-data.svg";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import Table from "components/Tablev2";
import { Menu, Transition } from "@headlessui/react";
import ApiService from "services/api.service";
import { toast } from "react-toastify";
import { useDebounce } from "react-use";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import SequenceBuilderModal, {
  SavedSequence,
} from "./Modals/SequenceBuilderModal";

interface SequenceApiItem {
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  templates: { id: number; name: string; type: string }[];
}

interface SequenceListItem {
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  templateCount: number;
  channels: string[];
}

interface SequenceTableProps {
  /** Counter that bumps when the parent wants to open the create-sequence modal. */
  createTrigger?: number;
  refreshSignal?: number;
  /** Called when the user wants to jump out of the sequence builder to create a template. */
  onRequestCreateTemplate?: () => void;
}

const SequenceTable: React.FC<SequenceTableProps> = ({
  createTrigger,
  refreshSignal,
  onRequestCreateTemplate,
}) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Debounce the search box into the value the query uses so typing doesn't
  // fire a request per keystroke.
  useDebounce(() => setDebouncedSearch(search), 300, [search]);

  const queryKey = [
    "sequences",
    { search: debouncedSearch.trim(), tag: tagFilter.trim(), refreshSignal },
  ];

  const { data, isPending, isError, isSuccess } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (tagFilter.trim()) params.set("tag", tagFilter.trim());
      const { data: response } = await ApiService.get<{
        data: SequenceApiItem[];
      }>({
        url: `/sequences${params.toString() ? `?${params}` : ""}`,
      });
      const rawRows = response?.data ?? [];
      return rawRows.map((r) => {
        const templates = r.templates ?? [];
        const channels = Array.from(
          new Set(templates.map((t) => t.type))
        ).sort();
        return {
          id: r.id,
          name: r.name,
          description: r.description,
          tags: r.tags ?? [],
          templateCount: templates.length,
          channels,
        };
      }) as SequenceListItem[];
    },
    // Keep the current rows on screen while the next request runs, so
    // searching/filtering never blanks the table.
    placeholderData: keepPreviousData,
  });

  const rows = data ?? [];
  const isLoading = isPending;

  useEffect(() => {
    if (isError) toast.error("Failed to load sequences");
  }, [isError]);

  // Other parts of the app dispatch this when they change sequences.
  useEffect(() => {
    const onChange = () => {
      queryClient.invalidateQueries({ queryKey: ["sequences"] });
    };
    window.addEventListener("leadstats:sequences-changed", onChange);
    return () =>
      window.removeEventListener("leadstats:sequences-changed", onChange);
  }, [queryClient]);

  useEffect(() => {
    if (createTrigger === undefined || createTrigger === 0) return;
    setEditingId(null);
    setIsModalOpen(true);
  }, [createTrigger]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.tags?.forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [rows]);

  // Delete a sequence optimistically: drop the row from every cached query,
  // roll back on failure, no list refetch.
  const deleteMutation = useMutation({
    mutationFn: (id: string) => ApiService.delete({ url: `/sequences/${id}` }),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["sequences"] });
      const snapshot = queryClient.getQueriesData<SequenceListItem[]>({
        queryKey: ["sequences"],
      });
      queryClient.setQueriesData<SequenceListItem[]>(
        { queryKey: ["sequences"] },
        (old) => (old ? old.filter((r) => r.id !== id) : old)
      );
      return { snapshot };
    },
    onSuccess: () => toast.success("Sequence deleted"),
    onError: (_err, _id, context) => {
      context?.snapshot.forEach(([key, value]) =>
        queryClient.setQueryData(key, value)
      );
      toast.error("Failed to delete sequence");
    },
  });

  const handleDelete = (id: string) => deleteMutation.mutate(id);

  const onEdit = (id: string) => {
    setEditingId(id);
    setIsModalOpen(true);
  };

  // Patch the saved sequence straight into the current view's cache instead of
  // re-running the (N+1 expand) list query.
  const handleSaved = (saved: SavedSequence, mode: "create" | "edit") => {
    const templates = saved.templates ?? [];
    const item: SequenceListItem = {
      id: saved.id,
      name: saved.name,
      description: saved.description,
      tags: saved.tags ?? [],
      templateCount: templates.length,
      channels: Array.from(new Set(templates.map((t) => t.type))).sort(),
    };
    queryClient.setQueryData<SequenceListItem[]>(queryKey, (old) => {
      if (!old) return old;
      return mode === "edit"
        ? old.map((r) => (r.id === item.id ? item : r))
        : [item, ...old];
    });
  };

  return (
    <div className="p-5 bg-themed-card rounded-lg flex flex-col gap-5">
      {rows.length === 0 && search === "" && tagFilter === "" && isSuccess ? (
        <div className="w-full h-[300px] flex items-center justify-center select-none">
          <div className="flex flex-col items-center gap-3">
            <img src={emptyDataImage} />
            <div className="font-inter text-[16px] font-semibold leading-[24px] text-themed-textSecondary">
              No sequences yet
            </div>
            <div className="text-[13px] text-themed-textSecondary text-center max-w-[420px]">
              A sequence is a saved bundle of templates the AI can reuse across
              workflows instead of recreating them from scratch.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-themed-textSecondary text-[13px]">
                Filter by tag:
              </span>
              <button
                className={`px-2 py-[2px] rounded text-[12px] border ${
                  tagFilter === ""
                    ? "border-[#6366F1] text-[#6366F1]"
                    : "border-themed-border text-themed-textSecondary"
                }`}
                onClick={() => setTagFilter("")}
              >
                All
              </button>
              {allTags.map((t) => (
                <button
                  key={t}
                  className={`px-2 py-[2px] rounded text-[12px] border ${
                    tagFilter === t
                      ? "border-[#6366F1] text-[#6366F1]"
                      : "border-themed-border text-themed-textSecondary"
                  }`}
                  onClick={() => setTagFilter(t)}
                >
                  {t}
                </button>
              ))}
            </div>
            {showSearch ? (
              <div className="flex gap-[10px] items-center">
                <Input
                  value={search}
                  onChange={setSearch}
                  placeholder="Search sequences"
                  showClearButton
                />
                <Button
                  type={ButtonType.LINK}
                  onClick={() => {
                    setShowSearch(false);
                    setSearch("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <button onClick={() => setShowSearch(true)}>
                <img src={searchIconImage} />
              </button>
            )}
          </div>

          <Table
            isLoading={isLoading}
            headings={[
              <div className="px-5 py-[10px] select-none">Name</div>,
              <div className="px-5 py-[10px] select-none">Tags</div>,
              <div className="px-5 py-[10px] select-none">Templates</div>,
              <div className="px-5 py-[10px] select-none">Channels</div>,
              <div className="px-5 py-[10px] select-none"></div>,
            ]}
            rowsData={rows}
            rows={rows.map((row) => [
              <button
                className="text-[#6366F1] text-left"
                onClick={() => onEdit(row.id)}
              >
                {row.name}
              </button>,
              <div className="flex flex-wrap gap-1">
                {(row.tags ?? []).map((t) => (
                  <span
                    key={t}
                    className="px-2 py-[1px] rounded bg-themed-background text-[12px]"
                  >
                    {t}
                  </span>
                ))}
              </div>,
              <div>{row.templateCount}</div>,
              <div className="flex gap-1">
                {row.channels.map((c) => (
                  <span
                    key={c}
                    className="px-2 py-[1px] rounded bg-themed-background text-[12px]"
                  >
                    {c}
                  </span>
                ))}
              </div>,
              <Menu as="div" className="relative">
                <Menu.Button>
                  <button className="px-[5px] py-[11px] rounded">
                    <img src={threeDotsIconImage} />
                  </button>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute z-[120] right-0 origin-top-right w-[200px] py-[4px] rounded-sm bg-themed-card shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`block w-full text-left py-[5px] px-[12px] ${
                            active ? "bg-themed-background" : ""
                          }`}
                          onClick={() => onEdit(row.id)}
                        >
                          Edit
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={`block w-full text-left py-[5px] px-[12px] text-[#F43F5E] ${
                            active ? "bg-themed-background" : ""
                          }`}
                          onClick={() => handleDelete(row.id)}
                        >
                          Delete
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>,
            ])}
          />
        </>
      )}

      <SequenceBuilderModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingId(null);
        }}
        onSaved={handleSaved}
        editingSequenceId={editingId}
        onRequestCreateTemplate={onRequestCreateTemplate}
      />
    </div>
  );
};

export default SequenceTable;
